import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { dict, loadLocale, type Locale } from './locales/_index';
import { detectInitialLocale } from './detect';
import { TAG } from './format';

/**
 * Keep `<html lang>` on the locale actually being rendered.
 *
 * index.html ships `lang="en"`, which is wrong the moment the page is not
 * English: screen readers pick the wrong voice, and Chrome offers to
 * translate a page that is already in the reader's language.
 */
export function syncDocumentLang(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = TAG[locale];
}

type State = { locale: Locale; setLocale: (l: Locale) => Promise<void> };

const isSupported = (l: unknown): l is Locale =>
  typeof l === 'string' && Object.prototype.hasOwnProperty.call(dict, l);

export const useLocaleStore = create<State>()(
  persist(
    (set) => ({
      locale: detectInitialLocale(),
      // Load the locale chunk first, then commit the change so useT()
      // immediately sees the correct strings on re-render.
      setLocale: async (locale) => {
        await loadLocale(locale);
        set({ locale });
        syncDocumentLang(locale);
      },
    }),
    {
      name: 'hg.locale',
      storage: createJSONStorage(() => localStorage),
      merge: (persisted, current) => {
        const p = persisted as Partial<State> | undefined;
        if (p?.locale && isSupported(p.locale)) return { ...current, ...p };
        return current;
      },
    },
  ),
);
